import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Plus, Search, Building2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card } from "../components/ui/card";
import YellowPageCard from "../components/yellow-pages/YellowPageCard";
import YellowPageDialog from "../components/yellow-pages/YellowPageDialog";

const CONTACT_TYPES = [
  { value: "ARCHITECT", label: "Architect", icon: "🏛️" },
  { value: "INTERIOR_DESIGNER", label: "Interior Designer", icon: "🎨" },
  { value: "LANDSCAPE_ARCHITECT", label: "Landscape Architect", icon: "🌳" },
  { value: "DEVELOPER", label: "Developer", icon: "🏗️" },
  { value: "BROKER", label: "Broker", icon: "🤝" },
  { value: "CONSULTANT", label: "Consultant", icon: "💼" },
  { value: "ENGINEER", label: "Engineer", icon: "⚙️" },
  { value: "OTHER", label: "Other", icon: "📋" }
];

export default function YellowPages() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showDialog, setShowDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, [typeFilter]);

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const params = typeFilter !== "all" ? `?contact_type=${typeFilter}` : "";
      const res = await apiClient.get(`/yellow-pages${params}`);
      setContacts(res.data);
    } catch (err) {
      toast.error("Failed to load contacts");
    }
    setLoading(false);
  };

  const filteredContacts = contacts.filter(c => {
    if (!search) return true;
    const s = search.toLowerCase();
    return c.company_name?.toLowerCase().includes(s) ||
           c.primary_contact?.toLowerCase().includes(s) ||
           c.email?.toLowerCase().includes(s);
  });

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await apiClient.delete(`/yellow-pages/${id}`);
      toast.success("Contact deleted");
      fetchContacts();
    } catch (err) {
      toast.error("Failed to delete contact");
    }
  };

  const openEdit = (contact) => {
    setEditingContact(contact);
    setShowDialog(true);
  };

  const openCreate = () => {
    setEditingContact(null);
    setShowDialog(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-light" style={{ fontFamily: 'var(--font-sans)' }}>Yellow Pages</h1>
          <p className="text-muted-foreground">Internal vendor & collaborator directory</p>
        </div>
        <Button onClick={openCreate} data-testid="add-contact-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Contact
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {CONTACT_TYPES.map(t => (
              <SelectItem key={t.value} value={t.value}>{t.icon} {t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      ) : filteredContacts.length === 0 ? (
        <Card className="p-12 text-center">
          <Building2 className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground">No contacts found</p>
          <Button variant="outline" className="mt-4" onClick={openCreate}>Add your first contact</Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredContacts.map(contact => (
            <YellowPageCard key={contact.id} contact={contact} onEdit={openEdit} onDelete={handleDelete} />
          ))}
        </div>
      )}

      <YellowPageDialog 
        open={showDialog} 
        onOpenChange={setShowDialog} 
        editingContact={editingContact}
        onSuccess={fetchContacts}
      />
    </div>
  );
}
